// Copyright (c) 2026 PGNS LLC

import type { HistoryDay } from '@/lib/api';
import { formatMs, latencySeries, polylinePoints } from '@/lib/status';

const WIDTH = 120;
const HEIGHT = 28;

/**
 * Response-time widget: latest p50/p95 plus a p95 sparkline over the window.
 *
 * p95 rather than an average, because an average hides exactly the tail a
 * status page is asked about. The sparkline is unlabelled by design — it shows
 * shape; the numbers next to it carry the magnitude.
 */
export function ResponseTime({ days }: { days: HistoryDay[] }) {
  const series = latencySeries(days);
  if (series.latestP95 === null) {
    return <p className="text-xs text-muted-foreground">No response times recorded.</p>;
  }
  return (
    <div className="flex items-center gap-4">
      {series.points.length > 0 && (
        <svg
          width={WIDTH}
          height={HEIGHT}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="text-brand-teal shrink-0"
          role="img"
          aria-label={`p95 response time trend, peaking at ${formatMs(series.max)}`}
        >
          <polyline
            points={polylinePoints(series.points, WIDTH, HEIGHT)}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}
      <dl className="flex gap-4 text-xs">
        <div>
          <dt className="text-muted-foreground">p50</dt>
          <dd className="font-mono text-foreground">{formatMs(series.latestP50)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">p95</dt>
          <dd className="font-mono text-foreground">{formatMs(series.latestP95)}</dd>
        </div>
      </dl>
    </div>
  );
}
